import { useState } from 'react'
import { clsx } from 'clsx'
import { Plus } from 'lucide-react'
import {
  useCreateSchedule,
  type CreateScheduleInput,
  type ScheduleAction,
  type ScheduleIntervalUnit,
  type ScheduleMode,
} from '../queries'
import styles from './GameweekScheduleCard.module.css'

type ScheduleFormProps = {
  gameweekId: string
}

const ACTIONS: Array<{ value: ScheduleAction; label: string }> = [
  { value: 'LOCK',     label: 'Lock' },
  { value: 'FETCH',    label: 'Fetch stats' },
  { value: 'PROCESS',  label: 'Process' },
  { value: 'FINALIZE', label: 'Finalize' },
  { value: 'REOPEN',   label: 'Reabrir janela' },
]

const MODES: Array<{ value: ScheduleMode; label: string }> = [
  { value: 'ONCE',     label: 'Uma vez (data/hora)' },
  { value: 'INTERVAL', label: 'Intervalo (a cada N)' },
  { value: 'WINDOW',   label: 'Janela (a cada N, entre início e fim)' },
  { value: 'CRON',     label: 'Cron (avançado)' },
]

export function ScheduleForm({ gameweekId }: ScheduleFormProps) {
  const [action,        setAction]        = useState<ScheduleAction>('FETCH')
  const [mode,          setMode]          = useState<ScheduleMode>('ONCE')
  const [runAt,         setRunAt]         = useState('')
  const [intervalValue, setIntervalValue] = useState(5)
  const [intervalUnit,  setIntervalUnit]  = useState<ScheduleIntervalUnit>('MINUTES')
  const [windowStart,   setWindowStart]   = useState('')
  const [windowEnd,     setWindowEnd]     = useState('')
  const [cron,          setCron]          = useState('')

  const create = useCreateSchedule(gameweekId)

  // datetime-local yields a tz-less local string; toISOString() normalises to UTC for the API.
  const toIso = (local: string) => new Date(local).toISOString()

  const isValid =
    mode === 'ONCE'     ? runAt !== '' :
    mode === 'INTERVAL' ? intervalValue >= 1 :
    mode === 'WINDOW'   ? intervalValue >= 1 && windowStart !== '' && windowEnd !== '' :
    /* CRON */            cron.trim() !== ''

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!isValid) return

    // Build only the keys the mode uses — never assign undefined (exactOptionalPropertyTypes).
    const input: CreateScheduleInput = { action, mode }
    if (mode === 'ONCE') input.runAt = toIso(runAt)
    if (mode === 'INTERVAL' || mode === 'WINDOW') {
      input.intervalValue = intervalValue
      input.intervalUnit  = intervalUnit
    }
    if (mode === 'WINDOW') {
      input.windowStart = toIso(windowStart)
      input.windowEnd   = toIso(windowEnd)
    }
    if (mode === 'CRON') input.cron = cron.trim()

    create.mutate(input)
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <p className={styles.formTitle}>Novo agendamento</p>

      <div className={styles.row}>
        <label className={styles.field}>
          <span className={styles.label}>Ação</span>
          <select className={styles.select} value={action}
            onChange={(e) => setAction(e.target.value as ScheduleAction)} disabled={create.isPending}>
            {ACTIONS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
        </label>

        <label className={styles.field}>
          <span className={styles.label}>Modo</span>
          <select className={styles.select} value={mode}
            onChange={(e) => setMode(e.target.value as ScheduleMode)} disabled={create.isPending}>
            {MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </label>
      </div>

      {mode === 'ONCE' && (
        <div className={styles.row}>
          <label className={styles.field}>
            <span className={styles.label}>Data e hora</span>
            <input type="datetime-local" className={styles.input} value={runAt}
              onChange={(e) => setRunAt(e.target.value)} disabled={create.isPending} />
          </label>
        </div>
      )}

      {(mode === 'INTERVAL' || mode === 'WINDOW') && (
        <div className={styles.row}>
          <label className={styles.field}>
            <span className={styles.label}>A cada</span>
            <input type="number" min={1} className={styles.input} value={intervalValue}
              onChange={(e) => setIntervalValue(Number(e.target.value))} disabled={create.isPending} />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Unidade</span>
            <select className={styles.select} value={intervalUnit}
              onChange={(e) => setIntervalUnit(e.target.value as ScheduleIntervalUnit)} disabled={create.isPending}>
              <option value="MINUTES">Minutos (1–59)</option>
              <option value="HOURS">Horas (1–23)</option>
            </select>
          </label>
        </div>
      )}

      {mode === 'WINDOW' && (
        <div className={styles.row}>
          <label className={styles.field}>
            <span className={styles.label}>Início da janela</span>
            <input type="datetime-local" className={styles.input} value={windowStart}
              onChange={(e) => setWindowStart(e.target.value)} disabled={create.isPending} />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Fim da janela</span>
            <input type="datetime-local" className={styles.input} value={windowEnd}
              onChange={(e) => setWindowEnd(e.target.value)} disabled={create.isPending} />
          </label>
        </div>
      )}

      {mode === 'CRON' && (
        <div className={styles.row}>
          <label className={clsx(styles.field, styles.fieldWide)}>
            <span className={styles.label}>Expressão cron (5 campos, ex: */5 * * * *)</span>
            <input type="text" className={styles.input} value={cron} placeholder="*/5 * * * *"
              onChange={(e) => setCron(e.target.value)} disabled={create.isPending} />
          </label>
        </div>
      )}

      <button type="submit" className={styles.submitBtn} disabled={!isValid || create.isPending}>
        {create.isPending ? <span className={styles.spinner} aria-hidden="true" /> : <Plus size={14} />}
        {create.isPending ? 'Agendando…' : 'Agendar'}
      </button>
    </form>
  )
}
